import { useEffect, useState, useMemo } from "react";
import { useFormikContext } from "formik";
import logger from '../../utils/logger';
import axios from "axios";
import { environment } from "../../env/environment";
import "../medication/Medication.css";
import TextVoiceSearchAllergies from "./TextVoiceSearchAllergies";
import { Upload, File } from "lucide-react";
import docIcon from '../../assets/icons/doc_icon.png';
import pdfIcon from '../../assets/icons/pdf_Icon.png';
import txtIcon from '../../assets/icons/txt_icon.png';
import { useRef } from "react";

const MedicationAllergies = ({ moduleData, onPharmacyExtracted }) => {
  const { setFieldValue, values } = useFormikContext();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [extractedTextFromFiles, setExtractedTextFromFiles] = useState({});
  const [processingFiles, setProcessingFiles] = useState(new Set());
  const [fileProgress, setFileProgress] = useState({});
  const [blobUrls, setBlobUrls] = useState({});
  const [voiceSelections, setVoiceSelections] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentVideoSrc, setCurrentVideoSrc] = useState();
  const [activeTab, setActiveTab] = useState("upload");
  const medicationTableRef = useRef(null);
  const onPharmacyExtractedRef = useRef(onPharmacyExtracted);
  const [errorAlerts, setErrorAlerts] = useState([]);
  const [manualForm, setManualForm] = useState({ brand: '', generic: '', type: 'Prescription', drugClass: '' });

  useEffect(() => {
    onPharmacyExtractedRef.current = onPharmacyExtracted;
  }, [onPharmacyExtracted]);

  // State to store restored base64 images
  const [restoredImages, setRestoredImages] = useState({});

  // Initialize data from Formik values
  useEffect(() => {
    if (isInitialized) return; // Already initialized, don't run again

    const savedData = values.allergiesdata;

    if (savedData) {
      // Restore voice selections
      if (savedData.VoiceInputResults && Array.isArray(savedData.VoiceInputResults) && savedData.VoiceInputResults.length > 0) {
        setVoiceSelections(savedData.VoiceInputResults);
      }

      // Restore extracted text from files
      if (savedData.extractedTextFromFiles && typeof savedData.extractedTextFromFiles === 'object' && Object.keys(savedData.extractedTextFromFiles).length > 0) {
        setExtractedTextFromFiles(savedData.extractedTextFromFiles);
      }

      // Restore base64 images if available
      if (savedData.imageBase64Data && Object.keys(savedData.imageBase64Data).length > 0) {
        setRestoredImages(savedData.imageBase64Data);
        logger.log('📷 [MedicationAllergies] Restored images:', Object.keys(savedData.imageBase64Data));
      }

      // Also check for base64 in individual file entries
      if (savedData.uploadedFiles && Array.isArray(savedData.uploadedFiles)) {
        const images = {};
        savedData.uploadedFiles.forEach(file => {
          if (file.base64 && file.fileKey) {
            images[file.fileKey] = file.base64;
          }
        });
        if (Object.keys(images).length > 0) {
          setRestoredImages(prev => ({ ...prev, ...images }));
        }
      }
    }

    setIsInitialized(true);
  }, [isInitialized, values.allergiesdata]);

  // Auto-scroll to medication table when new medications are added
  useEffect(() => {
    const filesCount = Object.values(extractedTextFromFiles)
      .flat()
      .reduce((acc, entry) => {
        const matched = entry.medicationMatches?.length || 0;
        const unmatched = entry.unmatchedMedications?.length || 0;
        return acc + matched + unmatched;
      }, 0);

    const totalMeds = filesCount + voiceSelections.length;

    // Only scroll if there are medications and component is initialized
    if (totalMeds > 0 && isInitialized && medicationTableRef.current) {
      setTimeout(() => {
        medicationTableRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }, 300);
    }
  }, [
    extractedTextFromFiles, 
    voiceSelections,
    isInitialized
  ]);

  // ✅ Helper function to validate pharmacy data is not empty/blank
  const isValidPharmacyData = (pharmacyDetails) => {
    if (!pharmacyDetails) return false;
    // A pharmacy is valid if it has at least a non-empty name
    const hasValidName = pharmacyDetails.name &&
                        typeof pharmacyDetails.name === 'string' &&
                        pharmacyDetails.name.trim() !== '';
    return hasValidName;
  };

  // Extract Pharmacy Details from Extracted text
  // ✅ FIX: Filter out blank/invalid pharmacy entries at the source
  const extractPharmacyDetails = useMemo(() => {
    const allPharmacyDetails = [];

    // Uploaded files से pharmacy details
    Object.values(extractedTextFromFiles).forEach(entries => {
      entries.forEach(entry => {
        if (entry.text) {
          try {
            const parsed = JSON.parse(entry.text);
            // ✅ Only add if pharmacy details are valid (non-empty name)
            if (parsed.pharmacyDetails && isValidPharmacyData(parsed.pharmacyDetails)) {
              allPharmacyDetails.push({
                ...parsed.pharmacyDetails,
                source: 'file',
                fileName: entry.fileName,
                entryKey: entry.entryKey
              });
            }
          } catch (e) {
            // JSON parse error - ignore
          }
        }
      });
    });

    return allPharmacyDetails;
  }, [extractedTextFromFiles]);

    // ✅ FIX: Use ref instead of direct callback
  useEffect(() => {
    if (isInitialized && onPharmacyExtractedRef.current) {
      onPharmacyExtractedRef.current(extractPharmacyDetails);
    }
  }, [extractPharmacyDetails, isInitialized]);

  // Memoize medication data to prevent unnecessary recalculations
  const medicationData = useMemo(() => {
    const extractedFromFilesMeds = Object.values(extractedTextFromFiles)
      .flat()
      .filter(entry => !entry.isError)
      .flatMap(entry => entry.medicationMatches || []);

    const extractedFilesCount = extractedFromFilesMeds.length;
    const extractedFilesFormatted = extractedFromFilesMeds
      .map((med, idx) => `${idx + 1}. ${med.class} - ${med.generic} - ${med.brand}`)
      .join('\n');

    const voiceCount = voiceSelections.length;
    const voiceFormatted = voiceSelections
      .map((med, idx) => {
        const parts = [
          med.generic || med.Generic,
          med.brand || med.Brand,
          med.type,
          med.form,
          med.strength,
          med.route,
          med.dose,
          med.units,
          med.frequency,
          med.prn,
          med.prn_reason,
          med.status
        ].filter(val => val && String(val).trim() !== '');
        return `${idx + 1}. ${parts.join(' - ')}`;
      })
      .join('\n');

    const allMedicationsFromFiles = extractedFromFilesMeds.map(m => `${m.class} - ${m.generic} - ${m.brand}`);
    const allMedicationsVoiceInput = voiceSelections
      .filter(item => item && (item.generic || item.Generic))
      .map(item => {
        const voiceParts = [
          `Generic: ${item.generic || item.Generic || 'N/A'}`,
          (item.brand || item.Brand) && `Brand: ${item.brand || item.Brand}`,
          item.type && `Type: ${item.type}`,
          item.form && `Form: ${item.form}`,
          item.strength && `Strength: ${item.strength}`,
          item.route && `Route: ${item.route}`,
          item.dose && `Dose: ${item.dose}`,
          item.units && `Units: ${item.units}`,
          item.frequency && `Frequency: ${item.frequency}`,
          item.prn === 'Yes' && `PRN: Yes`,
          item.prn_reason && `PRN Reason: ${item.prn_reason}`,
          item.status && `Status: ${item.status}`,
          item.notes && `Notes: ${item.notes}`
        ].filter(Boolean).join(', ');
        return voiceParts;
      });

    const totalCount = extractedFilesCount + voiceCount;

    return {
      uploadedFiles: uploadedFiles.map(f => ({
        name: f.file.name,
        size: f.file.size,
        type: f.file.type
      })),
      extractedTextFromFiles,
      extractedMedicationsFromFilesFormatted: extractedFilesFormatted,
      extractedMedicationsFromFilesCount: extractedFilesCount,
      VoiceInputResults: voiceSelections,
      voiceInputFormatted: voiceFormatted,
      voiceInputCount: voiceCount,
      totalMedicationsCount: totalCount,
      timestamp: new Date().toISOString(),
      filesMedicationList: allMedicationsFromFiles.join('\n'),
      voiceMedicationList: allMedicationsVoiceInput.join('\n')
    };
  }, [
    extractedTextFromFiles,
    voiceSelections,
    uploadedFiles,
  ]);

  // Update Formik values when medication data changes
  useEffect(() => {
    if (!isInitialized) return;

    setFieldValue('allergiesdata', medicationData);
    setFieldValue('allergieslistfromfiles', medicationData.filesMedicationList);
    setFieldValue('allergiesvoiceinputlist', medicationData.voiceMedicationList);
  }, [medicationData, isInitialized, setFieldValue]);

  // Create blob URLs for uploaded files
  useEffect(() => {
    const newBlobUrls = {};

    uploadedFiles.forEach(({ file, fileKey }) => {
      if (!blobUrls[fileKey]) {
        newBlobUrls[fileKey] = URL.createObjectURL(file);
      }
    });

    if (Object.keys(newBlobUrls).length > 0) {
      setBlobUrls(prev => ({ ...prev, ...newBlobUrls }));
    }
  }, [uploadedFiles, blobUrls]);

  const fields = Array.isArray(moduleData) ? moduleData : [];

  // Get available tabs from fields (only show tabs that have been dragged/dropped by admin)
  // Tab names are fixed - only visibility depends on what admin has added
  const availableTabs = useMemo(() => {
    const tabs = [];
    fields.forEach(item => {
      if (item.type === 'file' && !tabs.find(t => t.key === 'upload')) {
        tabs.push({ key: 'upload', label: 'Upload' });
      }
      if (item.type === 'audio' && !tabs.find(t => t.key === 'voice')) {
        tabs.push({ key: 'voice', label: 'Search & Voice' });
      }
    });
    tabs.push({ key: 'manual', label: 'Add Manually' });
    return tabs;
  }, [fields]);

  // Set default active tab to first available tab
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find(t => t.key === activeTab)) {
      setActiveTab(availableTabs[0].key);
    }
  }, [availableTabs, activeTab]);

  if (!moduleData) return <p className="error">Page not found</p>;

  const handleFileUpload = async (files, fieldId, type = "file") => {
    if (!files || files.length === 0) return;

    const currentField = fields.find(field => field.id === fieldId);
    const maxFiles = currentField?.maxFileUpload ? parseInt(currentField.maxFileUpload) : 5;
    const currentUploaded = uploadedFiles.filter(f => f.fileKey.startsWith(`${fieldId}-`));

    if (currentUploaded.length + files.length > maxFiles) {
      alert(`You can upload a maximum of ${maxFiles} files for this field.`);
      return;
    }

    const newFiles = Array.from(files);

    newFiles.forEach(async (file, index) => {
      const fileKey = `${fieldId}-${file.name}-${Date.now()}-${index}-${type}`;

      setUploadedFiles((prev) => [...prev, { file, fileKey }]);
      setProcessingFiles(prev => new Set([...prev, fileKey]));
      setFileProgress(prev => ({
        ...prev,
        [fileKey]: { stage: "uploading", percent: 5, isVisible: true }
      }));

      const formData = new FormData();
      formData.append("file", file);
      formData.append("fieldId", fieldId);
      formData.append("type", type);

      try {
        const res = await axios.post(
          `${environment.baseUrl}/api/extract-text`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (progressEvent) => {
              // Upload progress: 5% to 60%
              const uploadPercent = Math.round((progressEvent.loaded * 55) / progressEvent.total);
              setFileProgress(prev => ({
                ...prev,
                [fileKey]: { stage: "uploading", percent: 5 + uploadPercent, isVisible: true }
              }));
            }
          }
        );

        // Quick progress after upload completes - fast transitions
        setFileProgress(prev => ({ ...prev, [fileKey]: { stage: "extracting", percent: 70, isVisible: true }}));
        await new Promise(resolve => setTimeout(resolve, 100));
        setFileProgress(prev => ({ ...prev, [fileKey]: { stage: "extracting", percent: 80, isVisible: true }}));
        await new Promise(resolve => setTimeout(resolve, 80));
        setFileProgress(prev => ({ ...prev, [fileKey]: { stage: "searching", percent: 90, isVisible: true }}));
        await new Promise(resolve => setTimeout(resolve, 80));

        const extractedContent = res.data.extractedText || 'No text extracted';
        const matchesFound = res.data.matchesFound || 0;
        const fullResponse = res.data.response || '';
        const medicationMatches = res.data.detailedResults?.map(result => ({
          class: result.matches[0]?.Class || 'N/A',
          generic: result.matches[0]?.Generic || 'N/A',
          brand: result.matches[0]?.Brand || 'N/A',
          matchType: result.matchType || 'Unknown'
        })) || [];

        const entryKey = `${fieldId}-${file.name}-${Date.now()}`;

        setExtractedTextFromFiles(prev => ({
          ...prev,
          [fieldId]: [
            ...(prev[fieldId] || []),
            {
              fileName: file.name,
              entryKey: entryKey,
              fileKey: fileKey,
              text: extractedContent,
              medicationMatches: medicationMatches,
              foundMedications: res.data.foundMedications || [],
              unmatchedMedications: res.data.unmatchedMedications || [],
              matchesFound: matchesFound,
              fullResponse: fullResponse,
              processingMethod: res.data.processingMethod,
              fileSize: res.data.fileSize,
              timestamp: res.data.timestamp
            },
          ],
        }));

        // Complete immediately
        setFileProgress(prev => ({ ...prev, [fileKey]: { stage: "done", percent: 100, isVisible: true }}));

        // Hide progress bar after short delay
        setTimeout(() => {
          setFileProgress(prev => {
            const updated = { ...prev };
            delete updated[fileKey];
            return updated;
          });
        }, 800);

      } catch (err) {
        logger.error("❌ Error extracting text:", err);
        logger.error("❌ Error response data:", err.response?.data);

        setFileProgress(prev => ({ ...prev, [fileKey]: { stage: "error", percent: 100, isVisible: true }}));

        // Use user-friendly message from backend if available
        const errorData = err.response?.data;
        const errorMsg = errorData?.userMessage || errorData?.error || err.message;
        const errorCode = errorData?.errorCode || 'UNKNOWN';

        // Log error code for debugging
        if (errorCode === 'QUOTA_EXCEEDED') {
          logger.warn("⚠️ AI Service quota exceeded - please check OpenAI billing");
        }

        // Add to error alerts for display
        const alertId = `${fileKey}-${Date.now()}`;
        setErrorAlerts(prev => [...prev, {
          id: alertId,
          fileName: file.name,
          message: errorMsg,
          errorCode: errorCode
        }]);

        // Auto-dismiss after 10 seconds
        setTimeout(() => {
          setErrorAlerts(prev => prev.filter(alert => alert.id !== alertId));
        }, 10000);

        setExtractedTextFromFiles(prev => ({
          ...prev,
          [fieldId]: [
            ...(prev[fieldId] || []),
            {
              fileName: file.name,
              fileKey: fileKey,
              text: errorMsg,
              isError: true,
              errorCode: errorCode
            },
          ],
        }));

        setTimeout(() => {
          setFileProgress(prev => {
            const updated = { ...prev };
            delete updated[fileKey];
            return updated;
          });
        }, 2000);
      } finally {
        setProcessingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(fileKey);
          return newSet;
        });
      }
    });
  };

  const getFileIcon = (fileObj) => {
    const file = fileObj.file || fileObj;
    if (!file || !file.type) return <File size={24} color="#666" />;

    if (file.type === "application/pdf") {
      return <img src={pdfIcon} alt="pdf icon" className="w-70" />;
    }

    if (
      file.type === "application/msword" ||
      file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return <img src={docIcon} alt="doc icon" className="w-70" />;
    }

    if (file.type.startsWith("text/")) {
      return <img src={txtIcon} alt="txt icon" className="w-70" />;
    }

    return <File size={24} color="#666" />;
  };

  const isImage = (fileObj) => {
    const file = fileObj.file || fileObj;
    return file && file.type && file.type.startsWith("image/");
  };

  const handleRemoveFile = (fileKey, fileName, fieldId) => {
    if (blobUrls[fileKey]) {
      URL.revokeObjectURL(blobUrls[fileKey]);
      setBlobUrls(prev => {
        const updated = { ...prev };
        delete updated[fileKey];
        return updated;
      });
    }

    setUploadedFiles((prevFiles) => prevFiles.filter((item) => item.fileKey !== fileKey));

    setProcessingFiles((prev) => {
      const updated = new Set(prev);
      updated.delete(fileKey);
      return updated;
    });

    setFileProgress((prev) => {
      const updated = { ...prev };
      delete updated[fileKey];
      return updated;
    });

    setExtractedTextFromFiles((prev) => {
      const updated = { ...prev };
      if (updated[fieldId]) {
        updated[fieldId] = updated[fieldId].filter(
          (entry) => entry.fileName !== fileName
        );
        if (updated[fieldId].length === 0) {
          delete updated[fieldId];
        }
      }
      return updated;
    });
  };

  const renderProgressBar = (fileKey) => {
    const progress = fileProgress[fileKey];
    if (!progress || !progress.isVisible) return null;

    const { stage, percent } = progress;

    let label = "";
    let gradientColors = "linear-gradient(90deg, #007bff, #00c6ff)";

    if (stage === "uploading") {
      label = "Uploading";
      gradientColors = "linear-gradient(90deg, #007bff, #00c6ff)";
    } else if (stage === "extracting") {
      label = "Extracting";
      gradientColors = "linear-gradient(90deg, #17a2b8, #20c997)";
    } else if (stage === "searching") {
      label = "Searching";
      gradientColors = "linear-gradient(90deg, #ffc107, #ff9800)";
    } else if (stage === "done") {
      label = "Complete";
      gradientColors = "linear-gradient(90deg, #28a745, #20c997)";
    } else if (stage === "error") {
      label = "Failed";
      gradientColors = "linear-gradient(90deg, #dc3545, #c82333)";
    }

    return (
      <div className="upload-progress-container">
        <div className="upload-progress-bar-wrapper">
          <div
            className={`upload-progress-bar ${stage === 'done' ? 'complete' : ''} ${stage === 'error' ? 'error' : ''}`}
            style={{
              width: `${percent}%`,
              background: gradientColors
            }}
          />
          {stage !== 'done' && stage !== 'error' && (
            <div className="upload-progress-shimmer" />
          )}
        </div>
        <div className="upload-progress-info">
          <span className="upload-progress-label">{label}</span>
          <span className="upload-progress-percent">{percent}%</span>
        </div>
      </div>
    );
  };

  const handleRemoveSingleMedication = (fieldId, entryKey, medIndex) => {
    setExtractedTextFromFiles((prev) => {
      const updated = { ...prev };
      const entries = updated[fieldId];
      if (!entries) return prev;

      const newEntries = entries.map((entry) => {
        if (entry.entryKey === entryKey) {
          const meds = [...(entry.medicationMatches || [])];
          meds.splice(medIndex, 1);
          return { ...entry, medicationMatches: meds };
        }
        return entry;
      }).filter(entry => {
        return (entry.medicationMatches && entry.medicationMatches.length > 0) ||
          (entry.unmatchedMedications && entry.unmatchedMedications.length > 0) ||
          entry.isError;
      });

      if (newEntries.length > 0) {
        updated[fieldId] = newEntries;
      } else {
        delete updated[fieldId];
      }

      return updated;
    });
  };

  const handleRemoveUnmatchedMedication = (fieldId, entryKey, medIndex) => {
    setExtractedTextFromFiles((prev) => {
      const updated = { ...prev };
      if (updated[fieldId]) {
        updated[fieldId] = updated[fieldId].map(entry => {
          if (entry.entryKey === entryKey) {
            const newUnmatched = [...(entry.unmatchedMedications || [])];
            newUnmatched.splice(medIndex, 1);
            return {
              ...entry,
              unmatchedMedications: newUnmatched
            };
          }
          return entry;
        }).filter(entry => {
          return (entry.medicationMatches && entry.medicationMatches.length > 0) ||
            (entry.unmatchedMedications && entry.unmatchedMedications.length > 0) ||
            entry.isError;
        });

        if (updated[fieldId].length === 0) {
          delete updated[fieldId];
        }
      }
      return updated;
    });
  };

  const handleHelpIconClick = (field) => {
    const videoSrc = field.helpVideoUrl || field.helpVideo;

    if (videoSrc) {
      setCurrentVideoSrc(videoSrc);
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentVideoSrc(null);
  };

  const addManualMedication = async () => {
    const brand = manualForm.brand.trim();
    const generic = manualForm.generic.trim();
    if (!brand && !generic) return;

    const existingManual = values?.manualAllergies || [];
    const id = 'manual|' + (generic || brand) + '|' + brand;
    if (existingManual.some(m => m.id === id)) return;

    const newItem = { id, generic: generic || brand, brand: brand || '', type: manualForm.type, isManual: true };
    const updatedManual = [...existingManual, newItem];
    setFieldValue('manualAllergies', updatedManual);

    // Set formatted string for backend
    const formatted = updatedManual
      .map((med, i) => `${i + 1}. ${[med.type, med.generic, med.brand].filter(Boolean).join(' - ')}`)
      .join(' | ');
    setFieldValue('manualallergieslist', formatted);

    setManualForm({ brand: '', generic: '', type: 'Prescription', drugClass: '' });

    try {
      const tenantId = window.__TENANT_ID__ || localStorage.getItem('followup_tenant_id');
      fetch(`${environment.baseUrl}/api/manual-medications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(tenantId ? { 'X-Tenant-ID': String(tenantId) } : {}) },
        body: JSON.stringify({ medicationName: brand || generic, medicationType: 'allergy', generic, brand, drugClass: manualForm.drugClass, type: manualForm.type }),
      });
    } catch (err) { logger.error('Failed to submit manual allergy:', err); }
  };

  const renderManualTab = () => {
    if (activeTab !== 'manual') return null;
    return (
      <div className="medication-card">
        <div style={{ padding: '20px' }}>
          <h3 style={{ margin: '0 0 6px 0', color: '#1e293b', fontSize: '16px' }}>Add Allergy Manually</h3>
          <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 16px 0' }}>Can't find your allergy medication? Enter the details below. It will be sent for admin review.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Brand Name *</label>
              <input type="text" value={manualForm.brand} onChange={(e) => setManualForm(prev => ({ ...prev, brand: e.target.value }))} placeholder="e.g. Ubrelvy"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Generic Name</label>
              <input type="text" value={manualForm.generic} onChange={(e) => setManualForm(prev => ({ ...prev, generic: e.target.value }))} placeholder="e.g. Ubrogepant"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Type</label>
              <select value={manualForm.type} onChange={(e) => setManualForm(prev => ({ ...prev, type: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', backgroundColor: '#fff' }}>
                <option value="Prescription">Prescription</option>
                <option value="Non-Prescription">Non-Prescription</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Drug Class</label>
              <input type="text" value={manualForm.drugClass} onChange={(e) => setManualForm(prev => ({ ...prev, drugClass: e.target.value }))} placeholder="e.g. Antibiotic"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <button onClick={addManualMedication} disabled={!manualForm.brand.trim() && !manualForm.generic.trim()}
              style={{ padding: '12px 20px', backgroundColor: (!manualForm.brand.trim() && !manualForm.generic.trim()) ? '#d1d5db' : '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: (!manualForm.brand.trim() && !manualForm.generic.trim()) ? 'not-allowed' : 'pointer', width: '100%' }}>
              Add Allergy
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    return fields.map((item) => {
      let tabType = '';
      if (item.type === 'file') tabType = 'upload';
      else if (item.type === 'audio') tabType = 'voice';

      if (tabType !== activeTab) return null;

      return (
        <div key={item.id} className="medication-card">
          {item.type === "file" && (
            <>
              <div className="upload-area">
                <label htmlFor={`file-input-${item.id}`} className="upload-label">
                  <div className="camera-area">
                    <input
                      type="file"
                      accept={item.acceptedTypes}
                      id={`file-input-${item.id}`}
                      style={{ display: "none" }}
                      onChange={(e) => handleFileUpload(e.target.files, item.id, "file")}
                      multiple
                    />
                    <Upload size={50} />
                    <p>{item.label || "Upload Document"}</p>
                    <p className="accepted-files">{item.acceptedTypes}</p>
                  </div>
                </label>
                {(item.helpVideo || item.helpVideoUrl) && (
                  <div
                    className="help-container"
                    onClick={() => handleHelpIconClick(item)}
                    title="Click to view help video"
                  >
                    <div className="help-icon">?</div>
                    <p>Help</p>
                  </div>
                )}
              </div>
              <div className="preview-doc">
                {uploadedFiles.map((fileObj) => {
                  const { file, fileKey } = fileObj;
                  return (
                    <div key={fileKey} className="file-preview-item">
                      <button
                        onClick={() => handleRemoveFile(fileKey, file.name, item.id)}
                        className="image-remove-icon"
                      >
                        x
                      </button>

                      <div>
                        {isImage(fileObj) ? (
                          <img src={blobUrls[fileKey]} alt="Uploaded" width="130" height="80" />
                        ) : (
                          <div className="file-icon-name">
                            {getFileIcon(fileObj)}
                            <span style={{ marginLeft: "8px" }}>{file.name}</span>
                          </div>
                        )}
                      </div>

                      {renderProgressBar(fileKey)}
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {item.type === "audio" && (
            <div>
              <TextVoiceSearchAllergies
                key={`voice-allergies-${item.id}`}
                csvData={item.csvData || item.options || []}
                label={item.label}
                description={item.description}
                initialSelections={voiceSelections}
                onSelectionChange={(selectedMeds) => {
                  setVoiceSelections(selectedMeds);
                }}
              />

              {(item.helpVideo || item.helpVideoUrl) && (
                <div
                  onClick={() => handleHelpIconClick(item)}
                  className="help-icon p-absolute"
                  title="Click to view help video"
                >
                  ?
                </div>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <>
      <h1 className="txt-center" style={{marginBottom:'20px'}}>Medication Allergies</h1>

      {/* Error Alerts */}
      {errorAlerts.length > 0 && (
        <div style={{ maxWidth: '800px', margin: '0 auto 20px auto', padding: '0 15px' }}>
          {errorAlerts.map((alert) => (
            <div key={alert.id} style={{
              backgroundColor: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px'
            }}>
              <span style={{ color: '#dc2626', fontSize: '20px', lineHeight: '1' }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#dc2626', marginBottom: '4px', fontSize: '14px' }}>
                  {alert.fileName} - Processing Failed
                </div>
                <div style={{ color: '#7f1d1d', fontSize: '13px' }}>
                  {alert.message}
                </div>
              </div>
              <button
                onClick={() => setErrorAlerts(prev => prev.filter(a => a.id !== alert.id))}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#dc2626',
                  cursor: 'pointer',
                  fontSize: '20px',
                  padding: '0',
                  lineHeight: '1'
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="medication-main-container">
        <div className="tabs desktop">
          {availableTabs.map((tab) => (
            <button
              key={tab.key}
              className={`tabs-btn ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="page-container">
          <div className="page-wrapper desktop-medication-card">
            {renderTabContent()}
            {renderManualTab()}
          </div>

          <div className="tabs mobile">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'space-between', marginBottom: '10px' }}>
              {availableTabs.map((tab) => (
                <button
                  key={tab.key}
                  className={`tabs-btn ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(activeTab === tab.key ? '' : tab.key)}
                  style={{ width: availableTabs.length <= 2 ? '48%' : '48%' }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab && (
              <div className="accordion-content">
                <div className="page-container">
                  <div className="page-wrapper">
                    {renderTabContent()}
                    {renderManualTab()}
                  </div>
                </div>
              </div>
            )}
          </div>

          {showModal && currentVideoSrc && (
            <div
              className="video-container"
              onClick={handleCloseModal}
            >
              <div
                style={{
                  position: 'relative',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  padding: '20px',
                  maxWidth: '800px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflow: 'auto'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={handleCloseModal}
                  className="video-popup-close-btn"
                  title="Close"
                >
                  ✕
                </button>

                <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>How to Find your Medication</h3>

                <video
                  controls
                  autoPlay
                  className="video-tag"
                  src={currentVideoSrc}
                > 
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          )}

        </div>
      </div>
          <div className="current-medication-details" ref={medicationTableRef}>
            <div className="medication-summary" style={{marginTop:'0px'}}>
               Medication Allergies List
            </div>

            <div>
              <table className="medication-table">
                <thead>
                  <tr>
                    <th className="medication-th">Medication Name</th>
                    <th className="medication-th">Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {/* File Medications */}
                  {Object.entries(extractedTextFromFiles).map(([fieldId, entries]) =>
                    entries.map((entry) => (
                      <>
                        {entry.medicationMatches?.map((med, medIdx) => (
                          <tr key={`file-${entry.entryKey}-matched-${medIdx}`}>
                            <td className="medication-th">
                              {med.class || '-'} -- {med.generic || '-'} -- {med.brand || '-'}
                            </td>
                            <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>
                              <button
                                onClick={() => handleRemoveSingleMedication(fieldId, entry.entryKey, medIdx)}
                                className="medication-remove-btn"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}

                        {entry.unmatchedMedications?.map((unmatched, unmatchedIdx) => (
                          <tr
                            key={`file-${entry.entryKey}-unmatched-${unmatchedIdx}`}
                          >
                            <td className="medication-th">
                              {unmatched || '-'}
                            </td>
                            <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>
                              <button
                                onClick={() => handleRemoveUnmatchedMedication(fieldId, entry.entryKey, unmatchedIdx)}
                                className="medication-remove-btn"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </>
                    ))
                  )}

                  {/* Voice Input Selections */}
                  {voiceSelections?.map((med, idx) => (
                    <tr key={`voice-${idx}-${med.generic || med.Generic}-${med.brand || med.Brand}`}>
                      <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                        {[
                          med.generic || med.Generic,
                          med.brand || med.Brand,
                          med.type,
                          med.form,
                          med.strength,
                          med.route,
                          med.dose,
                          med.units,
                          med.frequency,
                          med.prn,
                          med.prn_reason,
                          med.status
                        ]
                          .filter(val => val && val.trim() !== '')
                          .join(' - ') || '-'}
                      </td>

                      <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>
                        <button
                          onClick={() => {
                            setVoiceSelections(prev => prev.filter((_, i) => i !== idx));
                          }}
                          className="medication-remove-btn"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}

                  {/* Manual Allergies */}
                  {(values?.manualAllergies || []).map((med, idx) => (
                    <tr key={`manual-${idx}-${med.brand}-${med.generic}`} style={{ backgroundColor: '#fffbeb' }}>
                      <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                        {[med.generic, med.brand, med.type].filter(Boolean).join(' -- ')}
                        <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24' }}>Pending Review</span>
                      </td>
                      <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>
                        <button
                          onClick={() => {
                            setFieldValue('manualAllergies', (values?.manualAllergies || []).filter((_, i) => i !== idx));
                          }}
                          className="medication-remove-btn"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
    </>
  );
};

export default MedicationAllergies;